import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import type { PrivacyMeta } from '../../models/privacy';
import {
  MENU_ZOOM_VAR,
  menuLayer,
  syncMenuLayer,
} from '../recents/menu-position';

/** Gap between the badge and the tooltip, in screen pixels. */
const TIP_GAP = 6;
/** Keep-out margin from the viewport edges when the tooltip is clamped. */
const VIEWPORT_PAD = 8;

interface Placement {
  /** Horizontal centre of the badge, in screen pixels. */
  readonly x: number;
  readonly top: number;
  readonly bottom: number;
  /** Flipped under the badge because the tooltip did not fit above it. */
  readonly below: boolean;
}

/**
 * A model's privacy tier, with its definition on hover.
 *
 * The tier names alone ("Anon", "Private", "TEE") are jargon: they say a
 * guarantee exists without saying what it is, which on a privacy control is
 * the part that matters. The definition used to ride along as a native
 * `title`, which meant a ~1s browser delay, unstyled OS chrome, and nothing at
 * all in the menu, where a Radix item swallows the hover. So it gets a real
 * tooltip.
 *
 * It portals into the shared menu layer rather than rendering in place, for
 * two reasons: the menu-row badges sit inside a Radix `Content` with
 * `overflow-y: auto`, which would clip a tooltip drawn inside it, and that
 * layer is the one place that already cancels the ambient UI zoom (see
 * `menu-position.ts`), so a screen-space coordinate written here lands where
 * it says.
 */
export default function PrivacyBadge({
  meta,
  variant = 'menu',
}: {
  readonly meta: PrivacyMeta;
  /** `footer` is the composer trigger; `menu` is a dropdown row. */
  readonly variant?: 'footer' | 'menu';
}) {
  const tipId = useId();
  const [placement, setPlacement] = useState<Placement | null>(null);
  const chipRef = useRef<HTMLSpanElement | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const hasHint = meta.definition.length > 0;
  const open = hasHint && placement != null;

  const show = useCallback(() => {
    const chip = chipRef.current;
    if (chip == null || !hasHint) return;
    // The zoom the layer cancels is written by the host onto an element, which
    // React never sees, so it can have changed since the layer was created.
    // Both menus in the app re-read it as they open; a badge is hoverable with
    // no menu open at all, so this is the tooltip's only chance to.
    syncMenuLayer();
    const rect = chip.getBoundingClientRect();
    setPlacement({
      x: rect.left + rect.width / 2,
      top: rect.top,
      bottom: rect.bottom,
      below: false,
    });
  }, [hasHint]);

  const hide = useCallback(() => setPlacement(null), []);

  // Placed after the DOM is committed and before it is painted, when the
  // tooltip's real box is known. Everything here works in screen pixels: the
  // anchor sits in the zoom-cancelled layer, so that is the space its
  // `left`/`top` are read in.
  useLayoutEffect(() => {
    const box = boxRef.current;
    const anchor = anchorRef.current;
    if (box == null || anchor == null || placement == null) return;

    // Write the unclamped position FIRST, every run. React only touches a
    // style property when the value it rendered changes, so a clamp left
    // behind by an earlier run would otherwise survive into a placement that
    // no longer needs it — and the measurement below would be taken against
    // the stale offset.
    const baseTop = placement.below ? placement.bottom : placement.top;
    anchor.style.left = `${placement.x}px`;
    anchor.style.top = `${baseTop}px`;

    const rect = box.getBoundingClientRect();

    // Flip under the badge when it does not fit above — but only if there is
    // genuinely more room down there, so a badge squeezed between two edges
    // does not flip into an even tighter gap.
    const spaceAbove = placement.top - VIEWPORT_PAD;
    const spaceBelow = window.innerHeight - placement.bottom - VIEWPORT_PAD;
    if (
      !placement.below &&
      rect.height + TIP_GAP > spaceAbove &&
      spaceBelow > spaceAbove
    ) {
      setPlacement({ ...placement, below: true });
      return;
    }

    // Slide it back inside each edge it overshot. When neither side had room
    // the vertical clamp pulls the box over the badge, which is the right
    // trade: a tooltip overlapping its own badge is still readable, one past
    // the viewport edge is not.
    const shiftX =
      rect.left < VIEWPORT_PAD
        ? VIEWPORT_PAD - rect.left
        : Math.min(window.innerWidth - VIEWPORT_PAD - rect.right, 0);
    const shiftY =
      rect.bottom > window.innerHeight - VIEWPORT_PAD
        ? window.innerHeight - VIEWPORT_PAD - rect.bottom
        : Math.max(VIEWPORT_PAD - rect.top, 0);
    if (shiftX !== 0) anchor.style.left = `${placement.x + shiftX}px`;
    if (shiftY !== 0) anchor.style.top = `${baseTop + shiftY}px`;
  }, [placement]);

  // A tooltip anchored to a measured position goes stale the moment anything
  // moves, and the pointer need not leave the badge for that to happen — so
  // `pointerleave` alone is not enough. Cheapest correct answer: dismiss it.
  //
  // A window `resize` used to be here too. It is the one case where the badge
  // moves and the user is plausibly still on it, but it is also the rarest by
  // a wide margin, and a resize already ends with a fresh boundary event under
  // the pointer. Three listeners that each answer a case seen in ordinary use
  // beat four where one is insurance.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    // Capture, so a scroll inside the menu panel counts too.
    window.addEventListener('scroll', hide, true);
    // The footer chip lives INSIDE the picker button, so clicking it opens the
    // menu without the pointer ever leaving the chip — no leave event, and the
    // tooltip would hang over the menu it just opened.
    window.addEventListener('pointerdown', hide, true);
    // The focus route has no pointer at all, so nothing else would close it.
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('pointerdown', hide, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, hide]);

  // The keyboard route to the definition, which the badge cannot own itself:
  // making the chip focusable would put a second tab stop inside the picker
  // button, and one control would answer to two. So the tooltip follows the
  // focus of whatever the chip sits in — a menu row arrowed onto, or the
  // composer trigger — which is where the keyboard already is.
  useEffect(() => {
    const host = chipRef.current?.closest('[role="menuitem"], button');
    if (host == null || !hasHint) return undefined;
    const onFocus = () => {
      // A click focuses the trigger on its way to opening the menu, and a
      // tooltip that flashes as the panel appears reads as a glitch. Only a
      // focus the browser itself considers keyboard-driven opens one.
      if (!matchesSafely(host, ':focus-visible')) return;
      show();
    };
    host.addEventListener('focus', onFocus);
    host.addEventListener('blur', hide);
    return () => {
      host.removeEventListener('focus', onFocus);
      host.removeEventListener('blur', hide);
    };
  }, [hasHint, show, hide]);

  const layer = open ? menuLayer() : null;

  return (
    <>
      <Chip
        ref={chipRef}
        data-tier={meta.tier ?? undefined}
        $footer={variant === 'footer'}
        aria-describedby={open ? tipId : undefined}
        onPointerEnter={show}
        onPointerLeave={hide}
      >
        {meta.label}
      </Chip>
      {layer != null &&
        placement != null &&
        createPortal(
          <TipAnchor ref={anchorRef}>
            <TipBox
              ref={boxRef}
              id={tipId}
              role="tooltip"
              // Which side it settled on, in the DOM: the flip is the one
              // decision here that a reader (or a test) cannot see from the
              // styles alone.
              data-placement={placement.below ? 'below' : 'above'}
              $below={placement.below}
            >
              {meta.definition}
            </TipBox>
          </TipAnchor>,
          layer,
        )}
    </>
  );
}

/**
 * `:focus-visible` is not in every engine we render on (nor in jsdom, where
 * the tests run), and an unknown selector makes `matches` throw rather than
 * return false. Treat "cannot tell" as focus-visible: showing the tooltip on a
 * focus that came from a click is a smaller failure than never showing it to
 * the keyboard.
 */
function matchesSafely(el: Element, selector: string): boolean {
  try {
    return el.matches(selector);
  } catch {
    return true;
  }
}

const Chip = styled.span<{ readonly $footer: boolean }>`
  /* In a menu row an auto margin pins the badge to the right edge; the
     composer trigger is shrink-to-fit, where that same margin would swallow
     the gap after the model name. */
  margin-left: ${({ $footer }) => ($footer ? '0' : 'auto')};
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 1px 5px;
  border-radius: 3px;
  /* The trigger is one click target, and a cursor that changes mid-button
     reads as two. */
  cursor: ${({ $footer }) => ($footer ? 'inherit' : 'help')};
  flex-shrink: 0;
  /* Neutral default for privacy labels with no known tier. */
  color: var(--text-label);
  background: var(--hover-bg);

  &[data-tier='tee'] {
    color: #1a7f37;
    background: rgba(26, 127, 55, 0.14);
  }
  &[data-tier='private'] {
    color: #2f6feb;
    background: rgba(47, 111, 235, 0.14);
  }
  &[data-tier='anon'] {
    color: #bb8009;
    background: rgba(187, 128, 9, 0.16);
  }
`;

/**
 * A zero-size point at the badge, in screen pixels — positioned entirely by
 * the layout effect above, which is also what clamps it. Splitting the anchor
 * from the box is what lets the box carry the zoom-back-up (mirroring how
 * Radix puts its transform on a wrapper and the scale on the surface inside).
 */
const TipAnchor = styled.span`
  position: fixed;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  z-index: 2100;
`;

const TipBox = styled.span<{ readonly $below: boolean }>`
  /* Scaled back up by the layer that cancelled the ambient zoom. */
  zoom: var(${MENU_ZOOM_VAR}, 1);
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  ${({ $below }) => ($below ? `top: ${TIP_GAP}px;` : `bottom: ${TIP_GAP}px;`)}
  width: max-content;
  max-width: 240px;
  padding: 6px 8px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-color-container);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  color: var(--text-primary);
  font-size: 0.7rem;
  line-height: 1.05rem;
  font-weight: 400;
  letter-spacing: normal;
  text-align: left;
  white-space: normal;
  /* Purely informational, and it sits over the menu it describes — never let
     it eat the hover or the click meant for a row. */
  pointer-events: none;
`;
